import type { AddSignee } from '@/types/AddSignee.ts';
import { SignatureType } from '@/types/enums/SignatureType.ts';
import { SigneeType } from '@/types/enums/SigneeType.ts';
import { SignStatus } from '@/types/enums/SignStatus.ts';
import type { Signee } from '@/types/Signee.ts';
import type { SigneePolicy } from '@/types/SigneePolicy.ts';
import type { AxiosResponse } from 'axios';
import {
  createDraftSigneeId,
  isDraftSigneeId
} from '@/components/create/composables/useParticipantsReconcile.ts';
import type {
  DocumentObserversRequest,
  DocumentRequest
} from '@/types/Document.ts';
import { SignatureMode } from '@/types/enums/SignatureMode.ts';
import { Jurisdiction } from '@/types/enums/Jurisdiction.ts';
import { AuthorityService } from '@/types/enums/AuthorityService.ts';
import { useEmitter } from '@/composables/useEmitter.ts';
import type { UpdateSignee } from '@/types/deepsign/UpdateSignee.ts';
import type { SigneeGroup } from '@/types/deepsign/SigneeGroup.ts';
import type { AddDocumentDeepbox } from '@/types/deepsign/AddDocumentDeepbox.ts';
import { SendMail } from '@/types/enums/SendMail.ts';
import { uploadDocumentToBeSealed } from '@/api/deepsign/documents-file-seal.ts';
import { useApi } from '@/composables/useApi.ts';
import { useDocumentWizard } from '@/composables/create/useDocumentWizard.ts';
import { updateDocumentSignee } from '@/api/deepsign/documents-signees.ts';
import {
  applyFileAutographPositionsToSignings,
  applyGroupSignatureFieldsLocally,
  buildSigningsFromAddSignee,
  findSigneeGroupForSignee,
  getDefaultSigneeFileId,
  getPrimarySigneeAutographPosition,
  getSigneeGroupSlotIndex,
  isSigneeGroupQuorumSet,
  persistSigneeGroupSignatureFields,
  resolveFileAutographPositionsForGroup
} from '@/utils/signeeSignings.ts';

export function useInviteesActions(store) {
  const emitter = useEmitter();
  const { deepSignAPI } = useApi();
  const createStore = store;
  const { registerDocument } = useDocumentWizard();

  // Builds a local-only Signee used while the signing-workflow dialog defers
  // persistence. It is replaced by the backend signee in reconcileParticipants().
  function buildDraftSignee(payload: AddSignee): Signee {
    const existingPolicy: SigneePolicy | undefined =
      createStore.signeesOrderedFlat?.[0]?.policy;
    const policy: SigneePolicy = existingPolicy
      ? { ...existingPolicy, canModifyAutographPosition: true }
      : {
          canModifyEmail: true,
          canModifyAutographPosition: true,
          canDelete: true,
          canResendInvitation: true
        };

    return {
      signeeId: createDraftSigneeId(),
      signeeType: payload?.signeeType || SigneeType.SIGN,
      email: payload?.email || null,
      signatureType: payload?.signatureType || SignatureType.SIGNATURE,
      signUrl: '',
      documentId: createStore.document!.documentId,
      viewedTime: null,
      completionTime: null,
      signStatus: SignStatus.PENDING,
      initiatorComment: null,
      signeeComment: payload?.comment || null,
      language: payload?.language || null,
      autographPosition: payload?.autographPosition || null,
      signings: buildSigningsFromAddSignee(
        payload,
        getDefaultSigneeFileId(createStore.document)
      ),
      signOrder: payload?.signOrder || 0,
      policy
    };
  }

  function createUpdateSignOrderPayload(inviteesGroups: Array<Array<Signee>>) {
    const filteredGroups = inviteesGroups.filter((group) => group.length > 0);
    return filteredGroups.map((group) => {
      return group.map((signee) => signee.signeeId);
    });
  }

  async function addObserver(
    documentId: string,
    data: DocumentObserversRequest
  ) {
    const response = await deepSignAPI.value.documentsObservers.create(
      documentId,
      data
    );
    if (createStore.document) {
      const observerIndex = createStore.document.observers.findIndex(
        (observer) => observer.observerId === response.data.observerId
      );
      if (observerIndex >= 0) {
        createStore.document.observers[observerIndex] = response.data;
      } else {
        createStore.document.observers.push(response.data);
      }
    }
  }

  async function updateDocumentObserver(
    documentId: string,
    observerId: string,
    data: DocumentObserversRequest
  ) {
    //TODO @daniel.henkiel missing api definition - fix api package response type from never to Observer
    const response = await deepSignAPI.value.documentsObservers.updateById(
      documentId,
      observerId,
      data
    );
    if (createStore.document) {
      const observerIndex = createStore.document.observers.findIndex(
        (observer) => observer.observerId === response.data.observerId
      );
      if (observerIndex >= 0) {
        createStore.document.observers[observerIndex] = response.data;
      }
      createStore.document.observers = [...createStore.document.observers];
    }
  }

  async function getSignOrder(documentId: string) {
    try {
      const newOrderContext =
        await deepSignAPI.value.documentsSignOrder.get(documentId);
      return newOrderContext.data?.context || '';
    } catch (e) {
      console.error(`Get sign order failed. Reason: ${e}`);
      return '';
    }
  }

  // Only ever called from the signing-workflow dialog (Invitees.vue and its
  // children), where persistence is always deferred: reconcileParticipants()
  // creates the real signee on Confirm. There is no context where this should
  // hit the API directly, so it always returns a local draft signee rather
  // than relying on the deferPersistence flag being correct at call time.
  async function addInvitee(_documentId: string, payload: AddSignee) {
    return { data: buildDraftSignee(payload) } as AxiosResponse<Signee>;
  }

  async function removeInvitee(documentId: string, signeeId: string) {
    // Draft-id signees exist only in the browser (created on the backend by
    // reconcileParticipants on Confirm), so they are removed locally no matter
    // what deferPersistence says — a DELETE for a draft- id can only 400.
    if (createStore.deferPersistence || isDraftSigneeId(signeeId)) {
      // Defer the delete — drop it from local state only. A draft-only signee
      // simply never gets created; a real one is deleted in reconcile.
      await createStore.removeInviteeFromLocalData(signeeId);
      return;
    }
    try {
      const response = await deepSignAPI.value.documentsSignees.deleteById(
        documentId,
        signeeId
      );
      await createStore.removeInviteeFromLocalData(signeeId);
      // removeInviteeFromLocalData only prunes `signeesOrdered`; the deleted
      // signee still lingers in `document.signeeGroups`, where it reads as an
      // unplaced phantom and keeps the sidebar's per-file missing-signature
      // badge lit even when every remaining signee is placed. Re-fetch the
      // groups so they match the server, mirroring the file-mutation refresh.
      await createStore.refreshSigneeGroups();
      return response;
    } catch (e) {
      return e;
    }
  }

  // Applies an `updateInvitee` payload to local state only, used while
  // persistence is deferred (see `updateInvitee`). A quorum member's position
  // is written to its group's shared slot (`signeeGroup.signings`) instead of
  // the signee's own `signings` — the else-branch below — since that's what
  // quorum placement/satisfaction checks read; otherwise a placed slot would
  // be permanently invisible rather than just delayed until a later sync.
  function applyDeferredInviteeUpdate(
    signee: Signee,
    signeeId: string,
    payload: Partial<UpdateSignee>
  ) {
    const hasAutographUpdate =
      payload?.autographPosition !== undefined ||
      payload?.fileAutographPositions !== undefined;
    const signeeGroup = findSigneeGroupForSignee(
      signeeId,
      createStore.document?.signeeGroups
    );

    if (hasAutographUpdate && isSigneeGroupQuorumSet(signeeGroup)) {
      const fileAutographPositions = resolveFileAutographPositionsForGroup(
        payload,
        getDefaultSigneeFileId(createStore.document)
      );
      applyGroupSignatureFieldsLocally(
        signeeGroup!,
        getSigneeGroupSlotIndex(signeeGroup!, signeeId),
        fileAutographPositions
      );
    } else {
      if (payload?.autographPosition !== undefined) {
        signee.autographPosition = payload.autographPosition ?? null;
      }
      if (payload?.fileAutographPositions !== undefined) {
        signee.signings = applyFileAutographPositionsToSignings(
          signee.signings ?? [],
          payload.fileAutographPositions
        );
        signee.autographPosition = getPrimarySigneeAutographPosition(
          signee,
          getDefaultSigneeFileId(createStore.document)
        );
      }
    }
    if (payload?.email !== undefined) {
      signee.email = payload.email ?? null;
    }
    if (payload?.comment !== undefined) {
      signee.signeeComment = payload.comment ?? null;
    }
  }

  async function updateInvitee(
    documentId: string,
    signeeId: string,
    payload: Partial<UpdateSignee>
  ) {
    // Draft-id signees exist only in the browser (created on the backend by
    // reconcileParticipants on Confirm), so they are updated locally no matter
    // what deferPersistence says — a PUT for a draft- id can only 400.
    if (createStore.deferPersistence || isDraftSigneeId(signeeId)) {
      // Defer the update — apply it to the local signee only.
      const signee = createStore.signeesOrderedFlat.find(
        (s: Signee) => s.signeeId === signeeId
      );
      if (signee) {
        applyDeferredInviteeUpdate(signee, signeeId, payload);
      }
      return { data: signee } as AxiosResponse<Signee>;
    }
    try {
      const hasAutographUpdate =
        payload?.autographPosition !== undefined ||
        payload?.fileAutographPositions !== undefined;
      const signeeGroup = findSigneeGroupForSignee(
        signeeId,
        createStore.document?.signeeGroups
      );
      const isQuorumGroup =
        hasAutographUpdate && isSigneeGroupQuorumSet(signeeGroup);

      const response = isQuorumGroup
        ? await updateInviteeForQuorumGroup(
            documentId,
            signeeId,
            signeeGroup!,
            payload
          )
        : await updateDocumentSignee(documentId, signeeId, {
            ...(payload?.autographPosition !== undefined && {
              autographPosition: payload.autographPosition
            }),
            ...(payload?.fileAutographPositions !== undefined && {
              fileAutographPositions: payload.fileAutographPositions
            }),
            email: payload?.email ?? null,
            comment: payload?.comment ?? null
          });
      return response;
    } catch (e) {
      return e;
    }
  }

  // Once a signee group has a quorum (requiredSignatures > 0), its members'
  // positions are shared, group-owned signature slots — sending
  // autographPosition/fileAutographPositions to the signee endpoint 403s.
  // Persist email/comment as usual and push the positions through
  // updateSigneeGroupSignatureField instead, then merge the applied changes
  // into the response locally (it doesn't reflect group-owned positions) so
  // the UI updates the same way as the non-quorum path.
  async function updateInviteeForQuorumGroup(
    documentId: string,
    signeeId: string,
    signeeGroup: SigneeGroup,
    payload: Partial<UpdateSignee>
  ): Promise<AxiosResponse<Signee>> {
    const response = await updateDocumentSignee(documentId, signeeId, {
      email: payload?.email ?? null,
      comment: payload?.comment ?? null
    });

    const defaultFileId = getDefaultSigneeFileId(createStore.document);
    const fileAutographPositions = resolveFileAutographPositionsForGroup(
      payload,
      defaultFileId
    );

    await persistSigneeGroupSignatureFields(
      documentId,
      signeeGroup,
      signeeId,
      fileAutographPositions
    );

    response.data.signings = applyFileAutographPositionsToSignings(
      response.data.signings ?? [],
      fileAutographPositions
    );
    response.data.autographPosition = getPrimarySigneeAutographPosition(
      response.data,
      defaultFileId
    );

    return response;
  }

  async function updateDocumentById(id: string, data: DocumentRequest) {
    await deepSignAPI.value.documents.updateById(id, data);
    if (createStore.document) {
      if (data.signeeAttachmentsAllowed) {
        createStore.document.signeeAttachmentsAllowed =
          data.signeeAttachmentsAllowed;
      }

      if (data.requiredAuthorityService !== undefined) {
        createStore.document.requiredAuthorityService =
          data.requiredAuthorityService;
      }
      if (data.jurisdiction) {
        createStore.document.jurisdiction = data.jurisdiction;
      }
      if (data.signatureMode) {
        createStore.document.signatureMode = data.signatureMode;
      }
    }
  }

  async function updateSignatureMessage(newMessage) {
    try {
      await updateDocumentById(createStore.document!.documentId, {
        comment: newMessage
      } as DocumentRequest);
    } catch (error) {
      console.error('Error adding document comment ', error);
    }
  }

  async function updateAttachmentsAllow(value) {
    if (value !== createStore.document!.signeeAttachmentsAllowed) {
      try {
        await updateDocumentById(createStore.document!.documentId, {
          signeeAttachmentsAllowed: value
        } as DocumentRequest);
        createStore.setSigneeAttachmentsAllowed(value);
      } catch (error) {
        console.error('error updating attachments option ', error);
      }
    }
  }

  async function updateSignatureLevel({ level, newJurisdiction }) {
    try {
      await updateDocumentById(createStore.document!.documentId, {
        signatureMode: level,
        jurisdiction: newJurisdiction,
        signeeAttachmentsAllowed: createStore.document!.signeeAttachmentsAllowed
      } as DocumentRequest);
      if (
        level === SignatureMode.ADVANCED &&
        newJurisdiction === Jurisdiction.EIDAS
      ) {
        emitter.$emit('set-authority-service', AuthorityService.DEEP_ID);
      } else {
        emitter.$emit('set-authority-service', undefined);
      }
    } catch (error) {
      console.error('error updating document signature level ', error);
    }
  }

  async function enforceAuthorityService(serviceKey) {
    try {
      await updateDocumentById(createStore.document!.documentId, {
        requiredAuthorityService: serviceKey
      } as DocumentRequest);
      createStore.setEnforceAuthorityService(serviceKey);
    } catch (error) {
      console.warn('Error enforcing authority service', error);
    }
  }

  async function updateAuthorityService(authorityService) {
    try {
      let jurisdiction = createStore.document!.jurisdiction;
      if (authorityService !== AuthorityService.DEEP_ID) {
        jurisdiction = Jurisdiction.ZERTES;
      }
      await updateDocumentById(createStore.document!.documentId, {
        authorityService,
        jurisdiction
      } as unknown as DocumentRequest);
    } catch (error) {
      console.error('error updating document authority service ', error);
    }
  }

  async function deleteObserver(documentId: string, observerId: string) {
    await deepSignAPI.value.documentsObservers.deleteById(
      documentId,
      observerId
    );
    if (createStore.document) {
      createStore.document.observers = createStore.document.observers.filter(
        (obs) => obs.observerId !== observerId
      );
    }
  }

  async function removeAllInviteeFieldsWithEmptyEmail() {
    for (const invitee of createStore.document!.signeesOrdered.flat()) {
      if (!invitee.email) {
        await createStore.removeInvitee(
          createStore.document!.documentId,
          invitee.signeeId
        );
      }
    }
  }

  async function removeAllObserversWithEmptyEmail() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promises: Array<Promise<any>> = [];
    for (const observer of createStore.document!.observers) {
      if (observer.email === null) {
        promises.push(
          createStore.deleteObserver(
            createStore.document!.documentId,
            observer.observerId
          )
        );
      }
    }
    await Promise.all(promises);
  }

  async function createDocumentFromDeepBox(data: AddDocumentDeepbox) {
    const response = await deepSignAPI.value.documentsDeepbox.create(data);
    createStore.document = response.data;
    registerDocument(response.data?.documentId);
  }

  async function createDocumentFromFile(file: File, companyId: string) {
    const data = {
      companyId: companyId || null,
      scanPredefinedSignees: true,
      addInitiatorAsSignee: true,
      sendMail: SendMail.OTHERS,
      authorityService: null,
      applyCompanySettingsDefaults: true,
      envelopeAllowed: true
      // Left here just for testing cases
      // autographRequired: true,
      // visualSignatureRequirement: 'required'
    };

    const response = await deepSignAPI.value.documentsFile.create(file, data);
    createStore.document = response.data;
    registerDocument(response.data?.documentId);
  }

  async function createSealDocumentFromFile(file: File, companyId: string) {
    const config = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    };

    const formData = new FormData();
    const data = JSON.stringify({
      companyId: companyId || null,
      scanPredefinedSignees: true,
      addInitiatorAsSignee: true,
      sendMail: SendMail.OTHERS,
      signatureMode: SignatureMode.TIMESTAMP,
      jurisdiction: Jurisdiction.ZERTES,
      authorityService: null,
      scanPredefined: true
    });
    formData.append('data', new Blob([data], { type: 'application/json' }));
    formData.append('file', file, file.name);

    const response = await uploadDocumentToBeSealed(formData, config);
    createStore.document = response.data;
  }

  return {
    addInvitee,
    addObserver,
    deleteObserver,
    createUpdateSignOrderPayload,
    createDocumentFromDeepBox,
    createSealDocumentFromFile,
    createDocumentFromFile,
    enforceAuthorityService,
    getSignOrder,
    updateDocumentById,
    updateDocumentObserver,
    updateSignatureMessage,
    updateAttachmentsAllow,
    updateAuthorityService,
    updateSignatureLevel,
    updateInvitee,
    removeInvitee,
    removeAllInviteeFieldsWithEmptyEmail,
    removeAllObserversWithEmptyEmail
  };
}
